<template>
  <el-drawer
    :title="menuData ? '修改菜单' : '新增菜单'"
    :visible.sync="visible"
    :direction="direction"
    :size="500"
    @close="handleClose">
    <div class="drawer-container">
      <el-form 
        ref="form" 
        :model="formData" 
        :rules="rules" 
        label-width="100px">
        <el-form-item label="上级菜单" prop="parentId">
          <el-cascader
            v-model="formData.parentId"
            :options="menuOptions"
            :props="{
              checkStrictly: true,
              value: 'id',
              label: 'title',
              emitPath: false,
              expandTrigger: 'hover'
            }"
            placeholder="请选择上级菜单"
            clearable
            style="width: 100%">
            <template slot-scope="{ node, data }">
              <span>{{ data.title }}</span>
              <span v-if="data.menuType">
                ({{ data.menuType }})
              </span>
            </template>
          </el-cascader>
        </el-form-item>
        <el-form-item label="菜单类型" prop="menuType">
          <el-select v-model="formData.menuType" placeholder="请选择菜单类型" style="width: 100%">
            <el-option label="目录" value="路径"></el-option>
            <el-option label="菜单" value="菜单"></el-option>
            <el-option label="按钮" value="按钮"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="菜单名称" prop="title">
          <el-input v-model="formData.title" placeholder="请输入菜单名称"></el-input>
        </el-form-item>
        <el-form-item label="路由名称" prop="routeName">
          <el-input v-model="formData.routeName" placeholder="请输入路由名称"></el-input>
        </el-form-item>
        <el-form-item label="路由地址" prop="path">
          <el-input v-model="formData.path" placeholder="请输入路由地址"></el-input>
        </el-form-item>
        <el-form-item label="组件路径" prop="component">
          <el-input v-model="formData.component" placeholder="请输入组件路径"></el-input>
        </el-form-item>
        <el-form-item label="权限标识" prop="permission">
          <el-input v-model="formData.permission" placeholder="请输入权限标识"></el-input>
        </el-form-item>
        <el-form-item label="菜单图标" prop="icon">
          <el-input v-model="formData.icon" placeholder="请选择图标">
            <template slot="prepend">
              <i :class="formData.icon" v-if="formData.icon"></i>
            </template>
            <el-button slot="append" icon="el-icon-picture-outline" @click="showIconSelect">
              选择图标
            </el-button>
          </el-input>
        </el-form-item>
        <el-form-item label="显示排序" prop="sort">
          <el-input-number v-model="formData.sort" :min="0" :max="999"></el-input-number>
        </el-form-item>
        <el-form-item label="菜单状态">
          <el-radio-group v-model="formData.status">
            <el-radio :label="1">显示</el-radio>
            <el-radio :label="0">隐藏</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <div class="drawer-footer">
        <el-button @click="handleClose">取 消</el-button>
        <el-button type="primary" @click="handleSubmit">确 定</el-button>
      </div>
    </div>
    <icon-select
      :visible.sync="iconSelectVisible"
      @select="handleIconSelect"
    />
  </el-drawer>
</template>

<script>
import request from '@/utils/request'
import IconSelect from '@/components/IconSelect/index.vue'

export default {
  name: 'MenuFormDrawer',
  components: {
    IconSelect
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    menuData: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      direction: 'rtl',
      formData: {
        id: '',
        parentId: '0',
        title: '',
        icon: '',
        path: '',
        component: '',
        sort: 0,
        menuType: '菜单',
        status: 1,
        routeName: '',
        permission: '',
        isFrame: 0
      },
      menuOptions: [],  // 上级菜单选项
      rules: {
        title: [
          { required: true, message: '请输入菜单名称', trigger: 'blur' }
        ],
        path: [
          { required: true, message: '请输入路由地址', trigger: 'blur' }
        ],
        menuType: [
          { required: true, message: '请选择菜单类型', trigger: 'change' }
        ]
      },
      iconSelectVisible: false
    }
  },
  watch: {
    menuData: {
      handler(val) {
        if (val) {
          this.formData = {
            id: val.id || '',
            menuId: val.menuId || '',
            parentId: val.parentId || '0',
            title: val.title || '',
            icon: val.icon || '',
            path: val.path || '',
            component: val.component || '',
            sort: val.sort || 0,
            menuType: val.menuType || '菜单',
            status: val.status || 1,
            routeName: val.routeName || '',
            permission: val.permission || '',
            isFrame: val.isFrame || 0
          }
        } else {
          this.formData = {
            id: '',
            menuId: '',
            parentId: '0',
            title: '',
            icon: '',
            path: '',
            component: '',
            sort: 0,
            menuType: '菜单',
            status: 1,
            routeName: '',
            permission: '',
            isFrame: 0
          }
        }
        this.$nextTick(() => {
          this.$refs.form?.clearValidate()
        })
      },
      immediate: true
    },
    visible: {
      handler(val) {
        if (val) {
          this.getMenuOptions()
        }
      },
      immediate: true
    }
  },
  methods: {
    async getMenuOptions() {
      try {
        const res = await request({
          url: '/api/v1/menu/all',
          method: 'POST'
        })
        
        if (res.status === 1 && res.data) {
          // 格式化数据并转换为树形结构
          const formattedData = this.formatMenuData(res.data)
          this.menuOptions = this.convertToTree(formattedData)
        }
      } catch (error) {
        console.error('获取菜单选项错误:', error)
        this.$message.error('获取菜单选项失败')
      }
    },
    
    // 格式化菜单数据
    formatMenuData(data) {
      return data.map(item => ({
        id: item.MenuID,
        parentId: item.ParentID,
        title: item.MenuName,
        menuType: item.MenuType === 'F' ? '路径' : 
                 item.MenuType === 'M' ? '菜单' : '按钮'
      })).filter(item => item.menuType !== '按钮') // 过滤掉按钮类型
    },

    // 将扁平数据转换为树形结构
    convertToTree(data) {
      const map = new Map()
      const result = []

      // 先把所有节点放入映射表
      data.forEach(item => {
        map.set(item.id, { ...item, children: [] })
      })

      // 遍历所有节点，建立父子关系
      data.forEach(item => {
        const node = map.get(item.id)
        if (!item.parentId || item.parentId === '0') {
          // 顶级节点直接放入结果数组
          result.push(node)
        } else {
          // 非顶级节点放入父节点的children数组
          const parent = map.get(item.parentId)
          if (parent) {
            parent.children.push(node)
          }
        }
      })

      // 添加一个顶级菜单选项
      result.unshift({
        id: '0',
        title: '顶级菜单',
        menuType: '目录'
      })

      return result
    },

    handleClose() {
      this.$emit('update:visible', false)
      this.$refs.form?.resetFields()
    },

    async handleSubmit() {
      try {
        await this.$refs.form.validate()
        this.$emit('submit', this.formData)
        this.handleClose()
      } catch (error) {
        console.error('表单验证失败:', error)
      }
    },

    showIconSelect() {
      this.iconSelectVisible = true
    },

    handleIconSelect(icon) {
      this.formData.icon = icon
    }
  }
}
</script>

<style lang="scss" scoped>
.drawer-container {
  padding: 20px;
  height: calc(100% - 100px);
  overflow-y: auto;
}

.drawer-footer {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 20px;
  background: #fff;
  text-align: right;
  border-top: 1px solid #e8e8e8;
}

/* 添加级联选择器的自定义样式 */
:deep(.el-cascader-node__label) {
  font-size: 14px;
}

:deep(.el-cascader-panel) {
  .el-cascader-node__postfix {
    font-size: 12px;
    color: #909399;
  }
}
</style> 